/* Resources — printer manuals, searchable by what a teacher would type (not in the handoff: built from the kit).
 * StudioCore desktop PrintResources parity (m11 #9): the page is locked to YOUR printer (printers Brightivity Helper
 * found on the school computers, a fresh network look, an IP, or a model picked by hand — desktop usePrinterLock),
 * with a banner to change it and the onboarding modal when none is known; the manual viewer zooms 50–300 % and
 * pages with the arrow keys; cards list their printer models; no result cap; desktop legal footer.
 * Data forked once from studioCloud/app (features/resources/resourcesMetadata.js); the HP manuals are served from
 * brightivity.net/manuals (same origin, CSP 'self'). */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import { TopBar } from '../app/AppShell.jsx';
import { runCommand } from '../api/bridge.js';
import { useDevices, useLocalComputer } from '../features/computers/computers.jsx';
import {
  allPrinterModels, categories, fuseOptions, getManualIdForFamily, getPrinterFamilyFromModel, manuals, printerFamilies, resources,
} from '../features/resources/resourcesMetadata.js';
import { Button, Card, Chip, EmptyState, Icon, Input, Modal, SearchInput, Spinner, Tag } from '../ui/index.jsx';
import '../features/resources/resources.css';
import { Trans, useTranslation } from 'react-i18next';

const CAT_COLOR = { setup: 'blue', paper: 'yellow', printing: 'purple', maintenance: 'green', troubleshooting: 'coral' };
const MANUAL_PICK_KEY = 'printResources.manualPick';
const SELECTED_IP_KEY = 'printResources.selectedPrinterIp';
const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* blocked */ } };
const IP_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** A printer (from a computer's list, discovery or an IP probe) → the lock entry, or null when not an HP model we have a manual for. */
function toLock(p, computer) {
  let family = getPrinterFamilyFromModel(p.model);
  if (!family && p.series) {
    const s = String(p.series).toUpperCase().replace(/^X(?=T)/, '');
    family = printerFamilies.find((f) => Array.isArray(f.printers) && f.printers.some((x) => x.toUpperCase().includes(s))) || null;
  }
  if (!family) return null;
  const manualId = getManualIdForFamily(family.id);
  return manualId ? { kind: 'discovered', ip: p.ip, model: p.model, familyId: family.id, manualId, computer: computer || null } : null;
}

/** Desktop usePrinterLock over Brightivity Helper: real printers win over a manual pick; a chosen IP narrows to one. */
function usePrinterLock() {
  const { devices, refresh } = useDevices(30000);
  const { local } = useLocalComputer(devices, refresh, 30000);
  const [manualPick, setPick] = useState(() => { try { const x = JSON.parse(read(MANUAL_PICK_KEY) || 'null'); return x && allPrinterModels.some((m) => m.model === x.model) ? x : null; } catch { return null; } });
  const [selectedIp, setSelectedIp] = useState(() => read(SELECTED_IP_KEY));
  const [extra, setExtra] = useState([]); // found by a fresh look / IP this visit
  const discovered = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const d of devices || []) for (const p of d.printerList || []) { const l = toLock(p, d.name); if (l && !seen.has(l.ip)) { seen.add(l.ip); out.push(l); } }
    for (const l of extra) if (!seen.has(l.ip)) { seen.add(l.ip); out.push(l); }
    return out;
  }, [devices, extra]);
  // The computers' own lists can be empty (a Helper before 0.1.12 dropped printers not seen for a day): ask the Helper on
  // this computer — else the first online one — for every saved printer once, and lock to those.
  const askedRef = useRef('');
  useEffect(() => {
    if (!devices || discovered.length) return;
    const dev = (local && devices.find((d) => d.id === local.deviceId && d.online)) || devices.find((d) => d.online);
    if (!dev || askedRef.current === dev.id) return;
    askedRef.current = dev.id;
    runCommand(dev.id, 'printer-list', { maxAgeMs: null }, { requireOnline: true, timeoutMs: 30000 }).then((r) => {
      if (!r.ok || !Array.isArray(r.result.printers)) return;
      const found = r.result.printers.map((p) => toLock(p, dev.name)).filter(Boolean);
      if (found.length) setExtra((x) => [...x, ...found.filter((f) => !x.some((y) => y.ip === f.ip))]);
    });
  }, [devices, discovered.length, local]);
  const loading = devices == null;
  let locked = [];
  let source = null;
  if (discovered.length) {
    const one = selectedIp ? discovered.filter((p) => p.ip === selectedIp) : [];
    locked = one.length ? one : discovered;
    source = 'discovered';
  } else if (manualPick) { locked = [{ kind: 'manual', ip: null, ...manualPick }]; source = 'manual'; }
  return {
    loading, locked, source, discovered, devices: devices || [],
    choose: (ip) => { write(SELECTED_IP_KEY, ip); setSelectedIp(ip); },
    pickModel: (m) => { const v = { model: m.model, familyId: m.familyId, manualId: m.manualId }; write(MANUAL_PICK_KEY, JSON.stringify(v)); setPick(v); },
    addFound: (l) => { setExtra((x) => [...x.filter((y) => y.ip !== l.ip), l]); write(SELECTED_IP_KEY, l.ip); setSelectedIp(l.ip); },
  };
}

function PrinterOnboarding({ open, lock, onClose }) {
  const { t } = useTranslation('bv');
  const online = lock.devices.filter((d) => d.online);
  const [dev, setDev] = useState(null);
  const device = online.find((d) => d.id === dev) || online[0] || null;
  const [look, setLook] = useState(null); // null | 'busy' | {printers} | {error}
  const [ip, setIp] = useState('');
  const [ipState, setIpState] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { if (open) { setLook(null); setIp(''); setIpState(null); setQ(''); } }, [open]);
  const discover = async () => {
    setLook('busy');
    const r = await runCommand(device.id, 'discover', {}, { requireOnline: true, timeoutMs: 120000 });
    setLook(r.ok ? { printers: (r.result.printers || []).map((p) => toLock(p, device.name)).filter(Boolean) } : { error: r.error || t('res.onb.noAnswer') });
  };
  const probe = async (e) => {
    e.preventDefault();
    const v = ip.trim();
    if (!v) { setIpState({ error: t('res.onb.enterIp') }); return; }
    if (!IP_RE.test(v)) { setIpState({ error: t('res.onb.badIp') }); return; }
    if (!device) { setIpState({ error: t('res.onb.openHelperFirst') }); return; }
    setIpState({ busy: true });
    const r = await runCommand(device.id, 'probe-ip', { ip: v, timeout: 5.0 }, { requireOnline: true, timeoutMs: 60000 });
    const l = r.ok && r.result.printer ? toLock(r.result.printer, device.name) : null;
    if (l) { lock.addFound(l); setIpState({ found: l }); onClose(true); } else setIpState({ error: r.ok ? t('res.onb.notHp') : t('res.onb.unreachable') });
  };
  const models = allPrinterModels.filter((m) => !q.trim() || m.displayName.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Modal open={open} title={t('res.onb.title')} onClose={() => onClose(false)} width={640}>
      <div className="bv-res__onb" data-testid="printer-onboarding">
        <p className="bv-muted">{t('res.onb.sub')}</p>
        {lock.discovered.length > 0 && (
          <div className="bv-res__onb-block">
            <div className="bv-label">{t('res.onb.onComputers')}</div>
            <div className="bv-res__onb-list">
              {lock.discovered.map((p) => (
                <button key={p.ip} type="button" className="bv-res__onb-item" onClick={() => { lock.choose(p.ip); onClose(true); }} data-testid="onb-printer">
                  <Icon name="print" size={20} /><span><b>{p.model}</b><span className="bv-small bv-muted">{p.ip}{p.computer ? ` · ${p.computer}` : ''}</span></span>
                </button>
              ))}
            </div>
          </div>
        )}
        {device ? (
          <div className="bv-res__onb-block">
            <div className="bv-label">{online.length > 1 ? t('res.onb.look') : t('res.onb.lookFrom', { name: device.name })}</div>
            {online.length > 1 && (
              <select className="bv-select" aria-label={t('print.computer')} value={device.id} onChange={(e) => setDev(e.target.value)}>
                {online.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <Button icon="refresh" onClick={discover} disabled={look === 'busy'} data-testid="onb-discover">{look === 'busy' ? t('res.onb.looking') : t('res.onb.lookNetwork')}</Button>
            {look && look.error && <p className="bv-error-text">{look.error}</p>}
            {look && look.printers && (look.printers.length === 0 ? <p className="bv-small bv-muted">{t('res.onb.noneAnswered')}</p> : (
              <div className="bv-res__onb-list">
                {look.printers.map((p) => (
                  <button key={p.ip} type="button" className="bv-res__onb-item" onClick={() => { lock.addFound(p); onClose(true); }} data-testid="onb-found">
                    <Icon name="print" size={20} /><span><b>{p.model}</b><span className="bv-small bv-muted">{p.ip}</span></span>
                  </button>
                ))}
              </div>
            ))}
            <form className="bv-res__onb-ip" onSubmit={probe}>
              <Input aria-label={t('res.onb.ipAria')} placeholder={t('res.onb.ipPlaceholder')} value={ip} onChange={(e) => setIp(e.target.value)} data-testid="onb-ip" />
              <Button type="submit" variant="blue" disabled={ipState && ipState.busy} data-testid="onb-ip-find">{ipState && ipState.busy ? t('res.onb.searching') : t('res.onb.findPrinter')}</Button>
            </form>
            {ipState && ipState.busy && <p className="bv-small bv-muted">{t('res.onb.lookingAt', { ip: ip.trim() })}</p>}
            {ipState && ipState.error && <p className="bv-error-text" role="alert">{ipState.error}</p>}
          </div>
        ) : <p className="bv-small bv-muted">{t('res.onb.openHelperAuto')}</p>}
        <div className="bv-res__onb-divider"><span>{t('res.onb.orNotConnected')}</span></div>
        <div className="bv-res__onb-block">
          <div className="bv-label">{t('res.onb.pickModel')}</div>
          <Input aria-label={t('res.onb.modelAria')} placeholder={t('res.onb.modelPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="onb-model" />
          <div className="bv-res__onb-models">
            {models.map((m) => <button key={m.model} type="button" className="bv-res__onb-model" onClick={() => { lock.pickModel(m); onClose(true); }}>{m.displayName}</button>)}
          </div>
          <p className="bv-small bv-muted"><Trans t={t} i18nKey="res.onb.whereModel" components={{ b: <b /> }} /></p>
        </div>
      </div>
    </Modal>
  );
}

function ManualViewer({ r, onClose }) {
  const { t } = useTranslation('bv');
  const m = manuals[r.manual];
  const first = r.page + (m ? m.pageOffset : 0);
  const last = Math.max(first, (r.endPage || r.page) + (m ? m.pageOffset : 0));
  const [page, setPage] = useState(first);
  const [zoom, setZoom] = useState(1); // 0.5–3 (desktop 50–300 %)
  const [state, setState] = useState({ loading: true });
  const doc = useRef(null);
  const canvas = useRef(null);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        doc.current = await pdfjs.getDocument({ url: `/manuals/${encodeURIComponent(m.file)}`, isEvalSupported: false }).promise;
        if (!gone) setState({ ready: true });
      } catch (e) { if (!gone) setState({ error: String(e.message || e) }); }
    })();
    return () => { gone = true; if (doc.current) doc.current.destroy(); };
  }, [m]);
  useEffect(() => {
    if (!state.ready || !doc.current || !canvas.current) return undefined;
    let task;
    (async () => {
      const p = await doc.current.getPage(Math.min(page, doc.current.numPages));
      const width = Math.min(820, canvas.current.parentElement.clientWidth - 8) * zoom;
      const vp1 = p.getViewport({ scale: 1 });
      const vp = p.getViewport({ scale: (width / vp1.width) * (window.devicePixelRatio || 1) });
      canvas.current.width = vp.width; canvas.current.height = vp.height;
      canvas.current.style.width = `${width}px`;
      task = p.render({ canvasContext: canvas.current.getContext('2d'), viewport: vp });
      await task.promise.catch(() => {});
    })();
    return () => task && task.cancel();
  }, [page, zoom, state.ready]);
  // Desktop keys: ← → page, Esc closes (Modal), + − zoom.
  useEffect(() => {
    const k = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if (e.key === 'ArrowLeft') setPage((x) => Math.max(first, x - 1));
      else if (e.key === 'ArrowRight') setPage((x) => Math.min(last, x + 1));
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(3, z + 0.25));
      else if (e.key === '-') setZoom((z) => Math.max(0.5, z - 0.25));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [first, last]);
  return (
    <Modal open title={r.title} onClose={onClose} width={900}>
      <div className="bv-manual" data-testid="manual-viewer">
        <div className="bv-manual__bar">
          <span className="bv-small bv-muted">{m ? m.name : t('res.manual')} · {t('res.manualPages', { from: r.page, to: r.endPage || r.page })}</span>
          <div className="bv-manual__nav">
            <Button size="sm" disabled={page <= first} onClick={() => setPage((x) => x - 1)} aria-label={t('res.prevPage')}>{t('common.previous')}</Button>
            <span className="bv-small" data-testid="manual-page">{t('library.pageOf', { n: page - first + 1, total: last - first + 1 })}</span>
            <Button size="sm" variant="blue" disabled={page >= last} onClick={() => setPage((x) => x + 1)} aria-label={t('res.nextPage')}>{t('common.next')}</Button>
          </div>
          <div className="bv-manual__zoom">
            <Button size="sm" disabled={zoom <= 0.5} onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} aria-label={t('res.zoomOut')}>−</Button>
            <span className="bv-small" data-testid="manual-zoom">{Math.round(zoom * 100)}%</span>
            <Button size="sm" disabled={zoom >= 3} onClick={() => setZoom((z) => Math.min(3, z + 0.25))} aria-label={t('res.zoomIn')}>+</Button>
          </div>
        </div>
        <div className="bv-manual__page">
          {state.loading && <Spinner label={t('res.loadingManual')} />}
          {state.error && <p className="bv-error-text">{t('res.manualFailed')} {state.error}</p>}
          <canvas ref={canvas} hidden={!state.ready} />
        </div>
        <p className="bv-small bv-muted">{t('res.tip')}</p>
      </div>
    </Modal>
  );
}

export default function ResourcesPage() {
  const { t } = useTranslation('bv');
  const lock = usePrinterLock();
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('all');
  const [open, setOpen] = useState(null);
  const [changing, setChanging] = useState(false);
  const manualIds = useMemo(() => new Set(lock.locked.map((p) => p.manualId)), [lock.locked]);
  const scoped = useMemo(() => {
    const seen = new Set();
    return resources.filter((r) => manualIds.has(r.manual) && !seen.has(r.id) && seen.add(r.id));
  }, [manualIds]);
  const fuse = useMemo(() => new Fuse(scoped, fuseOptions), [scoped]);
  const list = useMemo(() => {
    let out = query.trim() ? fuse.search(query.trim()).map((x) => x.item) : scoped;
    if (cat !== 'all') out = out.filter((r) => r.category === cat);
    return out;
  }, [query, cat, fuse, scoped]);
  const count = useCallback((id) => scoped.filter((r) => id === 'all' || r.category === id).length, [scoped]);
  const needPrinter = !lock.loading && lock.locked.length === 0;
  // Desktop: with no printer known the onboarding modal opens by itself (once per visit).
  const autoOpened = useRef(false);
  useEffect(() => { if (needPrinter && !autoOpened.current) { autoOpened.current = true; setChanging(true); } }, [needPrinter]);
  const lockLabel = lock.locked.length === 1
    ? `${lock.locked[0].model}${lock.locked[0].ip ? ` (${lock.locked[0].ip})` : ''}`
    : lock.locked.map((p) => p.model).join(', ');
  return (
    <div className="bv-content bv-resources">
      <TopBar title={t('nav.resources')} subtitle={t('res.subtitle')} />
      {lock.locked.length > 0 && (
        <Card className="bv-res__banner" data-testid="printer-banner">
          <Icon name="print" size={22} />
          <span><Trans t={t} i18nKey="res.helpFor" values={{ printer: lockLabel }} components={{ b: <b /> }} />{lock.source === 'manual' ? <span className="bv-muted"> · {t('res.pickedByHand')}</span> : null}</span>
          <Button size="sm" onClick={() => setChanging(true)} data-testid="change-printer">{t('res.changePrinter')}</Button>
        </Card>
      )}
      <Card className="bv-res__filters">
        <SearchInput placeholder={t('res.searchPlaceholder')} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={t('res.searchAria')} disabled={!lock.locked.length} />
        <div className="bv-res__chips" role="group" aria-label={t('res.topic')}>
          {categories.map((c) => <Chip key={c.id} selected={cat === c.id} count={count(c.id)} onClick={() => setCat(c.id)}>{c.label}</Chip>)}
        </div>
      </Card>
      {lock.locked.length > 0 && <p className="bv-libmain__count" data-testid="resources-count">{t('res.topics', { count: list.length })}</p>}
      {lock.loading ? (
        <Card><EmptyState icon="print" title={t('res.lookingForPrinter')} /></Card>
      ) : needPrinter ? (
        <Card><EmptyState icon="print" title={t('res.selectPrinter')} action={<Button variant="blue" icon="print" onClick={() => setChanging(true)}>{t('res.findMyPrinter')}</Button>}>{t('res.onlyMatching')}</EmptyState></Card>
      ) : list.length === 0 ? (
        <Card><EmptyState icon="resources" title={t('res.noneFound')}>{t('res.noneFoundBody')}</EmptyState></Card>
      ) : (
        <div className="bv-res__grid" data-testid="resources-grid">
          {list.map((r) => (
            <button key={r.id} type="button" className="bv-res__card" onClick={() => setOpen(r)}>
              <Tag color={CAT_COLOR[r.category] || 'blue'}>{(categories.find((c) => c.id === r.category) || {}).label || r.category}</Tag>
              <span className="bv-res__title">{r.title}</span>
              {r.description && <span className="bv-res__desc">{r.description}</span>}
              {r.printers && r.printers.length > 0 && <span className="bv-res__models" data-testid="card-models">{r.printers.slice(0, 3).join(', ')}{r.printers.length > 3 ? ` +${r.printers.length - 3}` : ''}</span>}
              <span className="bv-res__meta"><Icon name="resources" size={16} /> {manuals[r.manual] ? manuals[r.manual].name : t('res.manual')} · {t('library.upload.pageN', { n: r.page })}</span>
            </button>
          ))}
        </div>
      )}
      <footer className="bv-res__legal" data-testid="resources-footer">
        <p className="bv-small bv-muted">{t('res.legal1')} | <a href="https://support.hp.com/us-en/products/printers" target="_blank" rel="noreferrer">{t('res.legalLink')}</a></p>
        <p className="bv-small bv-muted">{t('res.legal2')}</p>
      </footer>
      {open && <ManualViewer r={open} onClose={() => setOpen(null)} />}
      <PrinterOnboarding open={changing} lock={lock} onClose={() => setChanging(false)} />
    </div>
  );
}
